import React from 'react';
import { User, CreditCard, AlertCircle, CheckCircle, Calculator, Save, Truck, X } from 'lucide-react';

const AddPaymentModal = ({
  isOpen,
  onClose,
  newPayment,
  setNewPayment,
  handleAddPayment,
  calculateBasePay,
  calculateCommission,
  calculateNetPayment,
  formatCurrency,
  isSubmitting = false
}) => {
  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900">Add New Payment</h3>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Form Fields */}
              <div className="lg:col-span-2 space-y-6">
                {/* Recipient Information */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                    <User className="w-4 h-4 mr-2" />
                    Recipient Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Recipient Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={newPayment.recipientName}
                        onChange={(e) => setNewPayment({...newPayment, recipientName: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter recipient name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Recipient Type <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={newPayment.recipientType}
                        onChange={(e) => setNewPayment({...newPayment, recipientType: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="driver">Driver</option>
                        <option value="dispatcher">Dispatcher</option>
                        <option value="admin">Admin Staff</option>
                        <option value="contractor">Contractor</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Payment Details */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                    <CreditCard className="w-4 h-4 mr-2" />
                    Payment Details
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Payment Type</label>
                      <select
                        value={newPayment.paymentType}
                        onChange={(e) => setNewPayment({...newPayment, paymentType: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="weekly_settlement">Weekly Settlement</option>
                        <option value="bi_weekly_salary">Bi-weekly Salary</option>
                        <option value="monthly_salary">Monthly Salary</option>
                        <option value="bonus">Bonus Payment</option>
                        <option value="commission">Commission</option>
                        <option value="expense_reimbursement">Expense Reimbursement</option>
                        <option value="advance">Cash Advance</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                      <select
                        value={newPayment.paymentMethod}
                        onChange={(e) => setNewPayment({...newPayment, paymentMethod: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="direct_deposit">Direct Deposit</option>
                        <option value="check">Check</option>
                        <option value="cash">Cash</option>
                        <option value="wire">Wire Transfer</option>
                        <option value="payroll_card">Payroll Card</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Pay Period Start <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        value={newPayment.payPeriodStart}
                        onChange={(e) => setNewPayment({...newPayment, payPeriodStart: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Pay Period End <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        value={newPayment.payPeriodEnd}
                        onChange={(e) => setNewPayment({...newPayment, payPeriodEnd: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Payment Date</label>
                      <input
                        type="date"
                        value={newPayment.paymentDate}
                        onChange={(e) => setNewPayment({...newPayment, paymentDate: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Payment Status</label>
                      <select
                        value={newPayment.status}
                        onChange={(e) => setNewPayment({...newPayment, status: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="pending">Pending</option>
                        <option value="processing">Processing</option>
                        <option value="paid">Paid</option>
                        <option value="failed">Failed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Work Performance (for drivers) */}
                {newPayment.recipientType === 'driver' && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                      <Truck className="w-4 h-4 mr-2" />
                      Work Performance
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Load Count</label>
                        <input
                          type="number"
                          value={newPayment.loadCount}
                          onChange={(e) => setNewPayment({...newPayment, loadCount: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          min="0"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Total Miles</label>
                        <input
                          type="number"
                          value={newPayment.totalMiles}
                          onChange={(e) => setNewPayment({...newPayment, totalMiles: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          min="0"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Gross Revenue</label>
                        <input
                          type="number"
                          value={newPayment.grossRevenue}
                          onChange={(e) => setNewPayment({...newPayment, grossRevenue: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          min="0"
                          step="0.01"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Payment Calculation Type */}
                <div className="bg-blue-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                    <Calculator className="w-4 h-4 mr-2 text-blue-500" />
                    Payment Calculation Type
                  </h4>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Select Calculation Method <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={newPayment.paymentCalculationType}
                      onChange={(e) => setNewPayment({...newPayment, paymentCalculationType: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="percentage">Percentage of Load Amount</option>
                      <option value="flat_rate">Flat Rate Salary</option>
                      <option value="mileage">Mileage-Based Payment</option>
                      <option value="owner_operator">Owner Operator (Load - Commission)</option>
                    </select>
                  </div>

                  {/* Conditional fields based on calculation type */}
                  {newPayment.paymentCalculationType === 'percentage' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Load Amount <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          value={newPayment.loadAmount}
                          onChange={(e) => setNewPayment({...newPayment, loadAmount: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          min="0"
                          step="0.01"
                          placeholder="Enter total load amount"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Percentage Rate <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            value={newPayment.percentageRate}
                            onChange={(e) => setNewPayment({...newPayment, percentageRate: e.target.value})}
                            className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            min="1"
                            max="100"
                            step="0.1"
                            placeholder="65"
                          />
                          <span className="absolute right-3 top-2 text-gray-500">%</span>
                        </div>
                      </div>
                      <div className="col-span-2">
                        <p className="text-xs text-blue-600">
                          Driver will receive: {formatCurrency((parseFloat(newPayment.loadAmount) || 0) * ((parseFloat(newPayment.percentageRate) || 0) / 100))}
                        </p>
                      </div>
                    </div>
                  )}

                  {newPayment.paymentCalculationType === 'flat_rate' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Salary Amount <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        value={newPayment.salaryAmount}
                        onChange={(e) => setNewPayment({...newPayment, salaryAmount: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        min="0"
                        step="0.01"
                        placeholder="Enter fixed salary amount"
                      />
                      <p className="text-xs text-gray-500 mt-1">Fixed amount regardless of loads or miles</p>
                    </div>
                  )}

                  {newPayment.paymentCalculationType === 'mileage' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Total Miles <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          value={newPayment.totalMiles}
                          onChange={(e) => setNewPayment({...newPayment, totalMiles: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          min="0"
                          step="1"
                          placeholder="Enter total miles driven"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Rate per Mile <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-2 text-gray-500">$</span>
                          <input
                            type="number"
                            value={newPayment.mileageRate}
                            onChange={(e) => setNewPayment({...newPayment, mileageRate: e.target.value})}
                            className="w-full px-3 py-2 pl-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            min="0"
                            step="0.01"
                            placeholder="0.60"
                          />
                        </div>
                      </div>
                      <div className="col-span-2">
                        <p className="text-xs text-blue-600">
                          Driver will receive: {formatCurrency((parseFloat(newPayment.totalMiles) || 0) * (parseFloat(newPayment.mileageRate) || 0))}
                        </p>
                      </div>
                    </div>
                  )}

                  {newPayment.paymentCalculationType === 'owner_operator' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Load Amount <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          value={newPayment.loadAmount}
                          onChange={(e) => setNewPayment({...newPayment, loadAmount: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          min="0"
                          step="0.01"
                          placeholder="Enter total load amount"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Commission Rate <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            value={newPayment.commissionRate}
                            onChange={(e) => setNewPayment({...newPayment, commissionRate: e.target.value})}
                            className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            min="0"
                            max="50"
                            step="0.1"
                            placeholder="10"
                          />
                          <span className="absolute right-3 top-2 text-gray-500">%</span>
                        </div>
                      </div>
                      <div className="col-span-2 space-y-1">
                        <p className="text-xs text-red-600">
                          Commission: -{formatCurrency((parseFloat(newPayment.loadAmount) || 0) * ((parseFloat(newPayment.commissionRate) || 0) / 100))}
                        </p>
                        <p className="text-xs text-blue-600">
                          Owner Operator will receive: {formatCurrency((parseFloat(newPayment.loadAmount) || 0) - ((parseFloat(newPayment.loadAmount) || 0) * ((parseFloat(newPayment.commissionRate) || 0) / 100)))}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Deductions */}
                <div className="bg-red-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-2 text-red-500" />
                    Deductions
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Fuel</label>
                      <input
                        type="number"
                        value={newPayment.deductions.fuel}
                        onChange={(e) => setNewPayment({
                          ...newPayment, 
                          deductions: {...newPayment.deductions, fuel: e.target.value}
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        min="0"
                        step="0.01"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Advances</label>
                      <input
                        type="number"
                        value={newPayment.deductions.advances}
                        onChange={(e) => setNewPayment({
                          ...newPayment, 
                          deductions: {...newPayment.deductions, advances: e.target.value}
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        min="0"
                        step="0.01"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Insurance</label>
                      <input
                        type="number"
                        value={newPayment.deductions.insurance}
                        onChange={(e) => setNewPayment({
                          ...newPayment, 
                          deductions: {...newPayment.deductions, insurance: e.target.value}
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        min="0"
                        step="0.01"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Toll</label>
                      <input
                        type="number"
                        value={newPayment.deductions.toll}
                        onChange={(e) => setNewPayment({
                          ...newPayment, 
                          deductions: {...newPayment.deductions, toll: e.target.value}
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        min="0"
                        step="0.01"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Pre-pass</label>
                      <input
                        type="number"
                        value={newPayment.deductions.prepass}
                        onChange={(e) => setNewPayment({
                          ...newPayment, 
                          deductions: {...newPayment.deductions, prepass: e.target.value}
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        min="0"
                        step="0.01"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">ELD</label>
                      <input
                        type="number"
                        value={newPayment.deductions.eld}
                        onChange={(e) => setNewPayment({
                          ...newPayment, 
                          deductions: {...newPayment.deductions, eld: e.target.value}
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        min="0"
                        step="0.01"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Other</label>
                      <input
                        type="number"
                        value={newPayment.deductions.other}
                        onChange={(e) => setNewPayment({
                          ...newPayment, 
                          deductions: {...newPayment.deductions, other: e.target.value}
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        min="0"
                        step="0.01"
                      />
                    </div>
                  </div>
                </div>

                {/* Bonuses */}
                <div className="bg-green-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                    <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                    Bonuses & Incentives
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Safety Bonus</label>
                      <input
                        type="number"
                        value={newPayment.bonuses.safety}
                        onChange={(e) => setNewPayment({
                          ...newPayment, 
                          bonuses: {...newPayment.bonuses, safety: e.target.value}
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        min="0"
                        step="0.01"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">On-Time Bonus</label>
                      <input
                        type="number"
                        value={newPayment.bonuses.onTime}
                        onChange={(e) => setNewPayment({
                          ...newPayment, 
                          bonuses: {...newPayment.bonuses, onTime: e.target.value}
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        min="0"
                        step="0.01"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Efficiency Bonus</label>
                      <input
                        type="number"
                        value={newPayment.bonuses.efficiency}
                        onChange={(e) => setNewPayment({
                          ...newPayment, 
                          bonuses: {...newPayment.bonuses, efficiency: e.target.value}
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        min="0"
                        step="0.01"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Summary */}
              <div className="lg:col-span-1">
                <div className="bg-blue-50 rounded-lg p-4 lg:self-start">
                  <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                    <Calculator className="w-4 h-4 mr-2 text-blue-500" />
                    Payment Summary
                  </h4>

                  <div className="space-y-3">
                    {/* Payment calculation breakdown by type */}
                    {newPayment.paymentCalculationType === 'percentage' && (
                      <>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Load Amount:</span>
                          <span className="font-medium">{formatCurrency(parseFloat(newPayment.loadAmount) || 0)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Percentage ({newPayment.percentageRate}%):</span>
                          <span className="font-medium">{formatCurrency(calculateBasePay())}</span>
                        </div>
                      </>
                    )}

                    {newPayment.paymentCalculationType === 'flat_rate' && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Salary Amount:</span>
                        <span className="font-medium">{formatCurrency(calculateBasePay())}</span>
                      </div>
                    )}

                    {newPayment.paymentCalculationType === 'mileage' && (
                      <>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Miles:</span>
                          <span className="font-medium">{(parseFloat(newPayment.totalMiles) || 0).toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Rate/Mile (${newPayment.mileageRate}):</span>
                          <span className="font-medium">{formatCurrency(calculateBasePay())}</span>
                        </div>
                      </>
                    )}

                    {newPayment.paymentCalculationType === 'owner_operator' && (
                      <>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Load Amount:</span>
                          <span className="font-medium">{formatCurrency(parseFloat(newPayment.loadAmount) || 0)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-red-600">Commission ({newPayment.commissionRate}%):</span>
                          <span className="font-medium text-red-600">-{formatCurrency(calculateCommission())}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Net from Load:</span>
                          <span className="font-medium">{formatCurrency(calculateBasePay())}</span>
                        </div>
                      </>
                    )}

                    <hr className="border-gray-200" />

                    <div className="text-sm text-gray-700 font-medium">Deductions:</div>
                    {Object.entries(newPayment.deductions).map(([key, value]) => (
                      <div key={key} className="flex justify-between text-sm">
                        <span className="text-red-600 capitalize">{key}:</span>
                        <span className="text-red-600">-{formatCurrency(parseFloat(value) || 0)}</span>
                      </div>
                    ))}

                    <div className="text-sm text-gray-700 font-medium">Bonuses:</div>
                    {Object.entries(newPayment.bonuses).map(([key, value]) => (
                      <div key={key} className="flex justify-between text-sm">
                        <span className="text-green-600 capitalize">{key}:</span>
                        <span className="text-green-600">+{formatCurrency(parseFloat(value) || 0)}</span>
                      </div>
                    ))}

                    <hr className="border-gray-300" />

                    <div className="flex justify-between font-bold text-lg">
                      <span>Net Payment:</span>
                      <span className="text-blue-600">{formatCurrency(calculateNetPayment())}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-4 mt-6 pt-6 border-t border-gray-200">
            <button
              onClick={onClose}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddPayment}
                disabled={isSubmitting}
                className={`px-6 py-2 rounded-lg flex items-center space-x-2 transition-colors ${
                  isSubmitting
                    ? 'bg-blue-400 text-white cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                <Save className="w-4 h-4" />
                <span>{isSubmitting ? 'Saving...' : 'Create Payment'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

  );
};

export default AddPaymentModal;
